import React, { useState, useEffect } from 'react';
import type { Edge } from 'reactflow';
import { X } from 'lucide-react';
import type { EdgeCondition } from './types';

interface EdgeConfigModalProps {
  edge: Edge | null;
  onClose: () => void;
  onSave: (edgeId: string, label: string | null, condition: EdgeCondition | null, priority: number) => void;
}

const OPERATORS = [
  { value: 'equals', label: 'Igual a (==)' },
  { value: 'not_equals', label: 'Diferente de (!=)' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
];

export const EdgeConfigModal: React.FC<EdgeConfigModalProps> = ({ edge, onClose, onSave }) => {
  const [label, setLabel] = useState('');
  const [useCondition, setUseCondition] = useState(false);
  const [field, setField] = useState('last_response');
  const [operator, setOperator] = useState<EdgeCondition['operator']>('equals');
  const [value, setValue] = useState('');
  const [priority, setPriority] = useState(0);

  useEffect(() => {
    if (edge) {
      setLabel(edge.label as string || '');
      setPriority((edge.data?.priority as number) || 0);

      const condition = edge.data?.condition as EdgeCondition | undefined;
      if (condition) {
        setUseCondition(true);
        setField(condition.field || 'last_response');
        setOperator(condition.operator || 'equals');
        setValue(condition.value || '');
      } else {
        setUseCondition(false);
        setField('last_response');
        setOperator('equals');
        setValue('');
      }
    }
  }, [edge]);

  if (!edge) return null;

  const handleSave = () => {
    const condition = useCondition ? { field, operator, value } : null;
    onSave(edge.id, label || null, condition, priority);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configurar Conexão
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Rótulo da Conexão (opcional)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder='Ex: "Se Sim", "Se Não"'
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Aparece na linha de conexão
            </p>
          </div>

          {/* Condition Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useCondition"
              checked={useCondition}
              onChange={(e) => setUseCondition(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="useCondition" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Adicionar condição (para nós CONDITION)
            </label>
          </div>

          {/* Condition Fields */}
          {useCondition && (
            <div className="space-y-3 pl-6 border-l-2 border-blue-300 dark:border-blue-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campo
                </label>
                <input
                  type="text"
                  value={field}
                  onChange={(e) => setField(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="last_response"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Variáveis: last_response, customer_name, dias_cancelamento, etc.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Operador
                </label>
                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value as EdgeCondition['operator'])}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor
                </label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder='Ex: "Sim", "Não"'
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Comparação case-insensitive
                </p>
              </div>
            </div>
          )}

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Prioridade
            </label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="0"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Maior prioridade = avaliada primeiro (0 = padrão)
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>💡 Dica:</strong> Use condições para criar fluxos com decisões.
              Conexões sem condição são usadas como caminho padrão (fallback).
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};
